import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDownIcon, ChevronRightIcon, ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import type {
  OntologyEntity,
  OntologyEntityType,
} from "./types";
import {
  ONTOLOGY_ROOT_IRIS,
  entityDisplayName,
  topLevelEntities,
} from "./types";
import { classNames } from "../../../ui/styles/classNames";
import { readStoredStringSet, writeStoredStringSet } from "../../../shared/storage/storedStringSet";
import AppButton from "../../../ui/buttons/AppButton";

interface OntologyHierarchyTreeProps {
  entities: OntologyEntity[];
  entityType: OntologyEntityType;
  selectedIri: string | null;
  storageKey: string;
  onSelect: (iri: string) => void;
  /** Terms a failing check names: each gets a marker. */
  failingIris: ReadonlySet<string> | null;
  t: (key: string, options?: Record<string, unknown>) => string;
}

export default function OntologyHierarchyTree({
  entities,
  entityType,
  selectedIri,
  storageKey,
  onSelect,
  failingIris,
  t,
}: OntologyHierarchyTreeProps) {
  const selectedNodeRef = useRef<HTMLButtonElement | null>(null);
  const entitiesByIri = useMemo(
    () => new Map(entities.map((entity) => [entity.iri, entity])),
    [entities]
  );
  const topLevel = useMemo(() => topLevelEntities(entities, entityType), [entities, entityType]);

  const [expanded, setExpanded] = useState<Set<string>>(
    () => readStoredStringSet(storageKey) ?? new Set([ONTOLOGY_ROOT_IRIS[entityType]]),
  );

  // When the entity type or the selection changes, open the root and the selected entity's
  // ancestors once; the user may close them again. Adjusted during render, not in an effect.
  const [expandedFor, setExpandedFor] = useState<{ entityType: string; selectedIri: string | null } | null>(null);
  if (expandedFor?.entityType !== entityType || expandedFor.selectedIri !== selectedIri) {
    setExpandedFor({ entityType, selectedIri });
    setExpanded((previous) => withRootAndAncestors(previous, ONTOLOGY_ROOT_IRIS[entityType], selectedIri, entitiesByIri));
  }

  useEffect(() => {
    writeStoredStringSet(storageKey, expanded);
  }, [expanded, storageKey]);

  useEffect(() => {
    if (!selectedIri || !selectedNodeRef.current) {
      return;
    }

    selectedNodeRef.current.scrollIntoView({
      block: "center",
      inline: "nearest",
      behavior: "smooth",
    });
  }, [expanded, entityType, selectedIri]);

  const toggle = (iri: string) => {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(iri)) {
        next.delete(iri);
      } else {
        next.add(iri);
      }
      return next;
    });
  };

  const renderNode = (entity: OntologyEntity, depth: number, visited: Set<string>) => {
    const childEntities = entity.children
      .map((childIri) => entitiesByIri.get(childIri))
      .filter((child): child is OntologyEntity => Boolean(child))
      .sort((left, right) => entityDisplayName(left).localeCompare(entityDisplayName(right)));
    const hasChildren = childEntities.length > 0;
    const isExpanded = expanded.has(entity.iri);
    const isSelected = selectedIri === entity.iri;

    return (
      <li key={entity.iri}>
        <div className="flex min-h-[32px] items-center gap-1" style={{ paddingLeft: `${depth * 16}px` }}>
          {hasChildren ? (
            <AppButton
              variant="ghost"
              size="xs"
              className="h-6 w-6 p-0"
              onClick={() => { toggle(entity.iri); }}
              aria-label={
                isExpanded
                  ? t("ontologyViewer.collapseEntity", { name: entityDisplayName(entity) })
                  : t("ontologyViewer.expandEntity", { name: entityDisplayName(entity) })
              }
            >
              {isExpanded ? (
                <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
              )}
            </AppButton>
          ) : (
            <span className="h-6 w-6 flex-shrink-0" />
          )}
          <button
            type="button"
            ref={isSelected ? selectedNodeRef : undefined}
            className={classNames(
              "min-w-0 flex-1 rounded px-2 py-1 text-left text-sm",
              isSelected
                ? "font-medium"
                : "app-action-link"
            )}
            style={isSelected ? { background: "var(--app-accent-soft)", color: "var(--app-text)" } : undefined}
            title={entity.iri}
            onClick={() => { onSelect(entity.iri); }}
          >
            <span className="flex min-w-0 items-center gap-1">
              <span className="truncate">{entityDisplayName(entity)}</span>
              {failingIris?.has(entity.iri) === true ? (
                <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0 text-[var(--app-danger)]" aria-label={t("ontologyViewer.checks.failsHere")} role="img" />
              ) : null}
            </span>
          </button>
        </div>
        {hasChildren && isExpanded ? (
          <ul>
            {childEntities.map((child) => {
              if (visited.has(child.iri)) {
                return null;
              }
              const nextVisited = new Set(Array.from(visited));
              nextVisited.add(child.iri);
              return renderNode(child, depth + 1, nextVisited);
            })}
          </ul>
        ) : null}
      </li>
    );
  };

  if (topLevel.length === 0) {
    return <p className="app-text-muted p-3 text-sm">{t("ontologyViewer.noEntities")}</p>;
  }

  return (
    <ul className="space-y-0.5">
      {topLevel.map((entity) => renderNode(entity, 0, new Set([entity.iri])))}
    </ul>
  );
}

function withRootAndAncestors(
  expanded: ReadonlySet<string>,
  rootIri: string,
  selectedIri: string | null,
  entitiesByIri: ReadonlyMap<string, OntologyEntity>,
): Set<string> {
  const next = new Set(expanded);
  next.add(rootIri);
  if (selectedIri === null) {
    return next;
  }
  const visited = new Set([selectedIri]);
  const visitParents = (iri: string) => {
    entitiesByIri.get(iri)?.parents.forEach((parentIri) => {
      if (visited.has(parentIri)) {
        return;
      }
      visited.add(parentIri);
      next.add(parentIri);
      visitParents(parentIri);
    });
  };
  visitParents(selectedIri);
  return next;
}
